import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import {
  Users,
  FileText,
  Clock,
  TrendingUp,
  DollarSign,
  Truck,
  Radio,
  ChevronRight,
  Package,
  RefreshCw,
  Inbox,
  Percent,
  AlertTriangle,
  Award,
  CalendarDays,
  LayoutDashboard,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import { usePermissionsContext } from '../contexts/PermissionsContext';
import { AgendaFretesPanel } from '../components/agenda/AgendaFretesPanel';

// ---------------------------------------------------------------------------
// Identidade: "torre de controle" — vermelho de sinalização como já usado na
// marca, base grafite/asfalto, e monoespaçada para tudo que é número/código
// (manifesto de carga, placa, guia). Nada de mock: cada número aqui vem do
// banco, e cada card sabe mostrar "sem dados ainda" em vez de inventar.
// ---------------------------------------------------------------------------

const STATUS_FALLBACK_COLORS = ['#dc2626', '#d97706', '#2563eb', '#059669', '#7c3aed', '#0891b2'];
const MESES_PT = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

type StatusSlice = { id: number; name: string; value: number; cor: string };
type MonthPoint = { name: string; faturamento: number; repasse: number };
type WeekPoint = { name: string; entregas: number };
type Alerta = {
  id: number;
  titulo: string;
  mensagem: string;
  tipo: string;
  created_at: string;
};
type MotoristaRank = { id: number; nome: string; osFinalizadas: number; repasseTotal: number };
type ContaVencida = { id: number; nome: string; valor: number; dt_vencimento: string };

function formatBRL(v: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
}

function tempoRelativo(iso: string) {
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'agora mesmo';
  if (min < 60) return `${min} min atrás`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h atrás`;
  const d = Math.floor(h / 24);
  return `${d}d atrás`;
}

function alertaEstilo(tipo: string) {
  switch (tipo) {
    case 'erro':
    case 'error':
      return { color: 'text-red-600', bg: 'bg-red-50', ring: 'ring-red-100' };
    case 'sucesso':
    case 'success':
      return { color: 'text-emerald-600', bg: 'bg-emerald-50', ring: 'ring-emerald-100' };
    case 'alerta':
    case 'warning':
      return { color: 'text-amber-600', bg: 'bg-amber-50', ring: 'ring-amber-100' };
    default:
      return { color: 'text-blue-600', bg: 'bg-blue-50', ring: 'ring-blue-100' };
  }
}

export function Dashboard() {
  const { hasPermission } = usePermissionsContext();
  const canViewFinancial = hasPermission('view_financial');

  const [activeTab, setActiveTab] = useState<'metricas' | 'agenda'>('agenda');
  const [loading, setLoading] = useState(true);
  const [lastSync, setLastSync] = useState<Date | null>(null);

  const [stats, setStats] = useState({
    pessoas: 0,
    osAbertas: 0,
    osAndamento: 0,
    osFinalizadas: 0,
    faturamentoTotal: 0,
    totalVeiculos: 0,
    veiculosEmRota: 0,
  });

  const [statusSlices, setStatusSlices] = useState<StatusSlice[]>([]);
  const [monthly, setMonthly] = useState<MonthPoint[]>([]);
  const [weekly, setWeekly] = useState<WeekPoint[]>([]);
  const [alertas, setAlertas] = useState<Alerta[]>([]);
  const [motoristas, setMotoristas] = useState<MotoristaRank[]>([]);
  const [inadimplencia, setInadimplencia] = useState<{ total: number; count: number; contas: ContaVencida[] }>({
    total: 0,
    count: 0,
    contas: [],
  });
  const [margemPct, setMargemPct] = useState<number | null>(null);

  const totalOS = useMemo(
    () => stats.osAbertas + stats.osAndamento + stats.osFinalizadas,
    [stats]
  );

  const fetchStats = async () => {
    try {
      setLoading(true);

      // 1. Contagens simples
      const [{ count: countPessoas }, { count: countVeiculos }] = await Promise.all([
        supabase.from('pessoa').select('*', { count: 'exact', head: true }),
        supabase.from('veiculos').select('*', { count: 'exact', head: true }),
      ]);

      // 2. Metadados de status (nome/cor reais cadastrados no sistema)
      const { data: statusData } = await supabase.from('status').select('id, status, cor');
      const statusMap = new Map<number, { status: string; cor: string }>();
      (statusData || []).forEach((s) => statusMap.set(Number(s.id), { status: s.status, cor: s.cor }));

      // 3. Todas as OS, paginado, com os campos que alimentam KPIs + gráficos
      //    (uma única leitura reaproveitada em tudo, em vez de 3 consultas separadas)
      let allOS: any[] = [];
      let from = 0;
      const step = 1000;
      while (true) {
        const { data, error } = await supabase
          .from('ordem_de_servico')
          .select('status_id, total_os, valor_total_repasses, veiculos_id, data_atendimento, created_at')
          .range(from, from + step - 1);
        if (error) {
          console.warn('[Dashboard] erro ao paginar ordem_de_servico:', error.message);
          break;
        }
        if (!data || data.length === 0) break;
        allOS = allOS.concat(data);
        if (data.length < step) break;
        from += step;
      }

      // 4. KPIs por status + distribuição real
      let osAbertas = 0;
      let osAndamento = 0;
      let osFinalizadas = 0;
      let faturamentoTotal = 0;
      let repasseTotalGeral = 0;
      const statusGroups: Record<number, number> = {};

      allOS.forEach((os) => {
        const sId = Number(os.status_id);
        faturamentoTotal += Number(os.total_os) || 0;
        repasseTotalGeral += Number(os.valor_total_repasses) || 0;
        statusGroups[sId] = (statusGroups[sId] || 0) + 1;

        const sName = (statusMap.get(sId)?.status || '').toLowerCase();
        if (sName.includes('abert') || sName.includes('pendente') || sName.includes('solicitad') || sName.includes('agendad')) {
          osAbertas++;
        } else if (
          sName.includes('final') ||
          sName.includes('conclu') ||
          sName.includes('faturad') ||
          sName.includes('entregue')
        ) {
          osFinalizadas++;
        } else {
          osAndamento++;
        }
      });

      const slices: StatusSlice[] = Object.entries(statusGroups)
        .map(([sIdStr, count], i) => {
          const sId = Number(sIdStr);
          const meta = statusMap.get(sId);
          return {
            id: sId,
            name: meta?.status || `Status #${sId}`,
            value: count,
            cor: meta?.cor && /^#/.test(meta.cor) ? meta.cor : STATUS_FALLBACK_COLORS[i % STATUS_FALLBACK_COLORS.length],
          };
        })
        .sort((a, b) => b.value - a.value);
      setStatusSlices(slices);

      // 4b. Margem líquida geral: (faturamento - repasse) / faturamento
      setMargemPct(faturamentoTotal > 0 ? ((faturamentoTotal - repasseTotalGeral) / faturamentoTotal) * 100 : null);

      // 5. Faturamento x repasse dos últimos 6 meses (dado real, não mockado)
      const now = new Date();
      const buckets: Record<string, MonthPoint> = {};
      const order: string[] = [];
      for (let i = 5; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const key = `${d.getFullYear()}-${d.getMonth()}`;
        order.push(key);
        buckets[key] = { name: MESES_PT[d.getMonth()], faturamento: 0, repasse: 0 };
      }
      allOS.forEach((os) => {
        const dateStr = os.data_atendimento || os.created_at;
        if (!dateStr) return;
        const d = new Date(dateStr);
        const key = `${d.getFullYear()}-${d.getMonth()}`;
        if (buckets[key]) {
          buckets[key].faturamento += Number(os.total_os) || 0;
          buckets[key].repasse += Number(os.valor_total_repasses) || 0;
        }
      });
      setMonthly(order.map((k) => buckets[k]));

      // 6. Volume de OS finalizadas por semana (últimas 4 semanas, dado real)
      const weekBuckets: WeekPoint[] = [0, 1, 2, 3].map((i) => ({ name: `Semana ${i + 1}`, entregas: 0 }));
      const fourWeeksAgo = new Date(now.getTime() - 28 * 24 * 60 * 60 * 1000);
      allOS.forEach((os) => {
        const sName = (statusMap.get(Number(os.status_id))?.status || '').toLowerCase();
        const isFinalizada = sName.includes('final') || sName.includes('conclu') || sName.includes('entregue');
        if (!isFinalizada) return;
        const dateStr = os.data_atendimento || os.created_at;
        if (!dateStr) return;
        const d = new Date(dateStr);
        if (d < fourWeeksAgo || d > now) return;
        const weekIdx = Math.min(3, Math.floor((d.getTime() - fourWeeksAgo.getTime()) / (7 * 24 * 60 * 60 * 1000)));
        weekBuckets[weekIdx].entregas += 1;
      });
      setWeekly(weekBuckets);

      // 7. Veículos em rota agora: ping de rastreamento nos últimos 15 min com online = true
      const quinzeMinAtras = new Date(now.getTime() - 15 * 60 * 1000).toISOString();
      const { data: rastreio } = await supabase
        .from('rastreamento_motorista')
        .select('motorista_id')
        .eq('online', true)
        .gte('created_at', quinzeMinAtras);
      const veiculosEmRota = new Set((rastreio || []).map((r) => r.motorista_id)).size;

      // 7b. Ranking de motoristas por OS finalizada (junta veiculos -> motorista em memória,
      //     evita depender de uma view/join pronta no banco)
      const { data: veiculosData } = await supabase.from('veiculos').select('id, motorista_id');
      const veiculoToMotorista = new Map<string, number>();
      (veiculosData || []).forEach((v) => {
        if (v.motorista_id != null) veiculoToMotorista.set(v.id, v.motorista_id);
      });

      const motoristaCounts: Record<number, { osFinalizadas: number; repasseTotal: number }> = {};
      allOS.forEach((os) => {
        const sName = (statusMap.get(Number(os.status_id))?.status || '').toLowerCase();
        const isFinalizada = sName.includes('final') || sName.includes('conclu') || sName.includes('entregue');
        if (!isFinalizada || !os.veiculos_id) return;
        const motoristaId = veiculoToMotorista.get(os.veiculos_id);
        if (motoristaId == null) return;
        if (!motoristaCounts[motoristaId]) motoristaCounts[motoristaId] = { osFinalizadas: 0, repasseTotal: 0 };
        motoristaCounts[motoristaId].osFinalizadas += 1;
        motoristaCounts[motoristaId].repasseTotal += Number(os.valor_total_repasses) || 0;
      });

      const topMotoristaIds = Object.entries(motoristaCounts)
        .sort((a, b) => b[1].osFinalizadas - a[1].osFinalizadas)
        .slice(0, 5)
        .map(([id]) => Number(id));

      if (topMotoristaIds.length > 0) {
        const { data: motoristasNomes } = await supabase
          .from('pessoa')
          .select('id, nome')
          .in('id', topMotoristaIds);
        const nomeMap = new Map((motoristasNomes || []).map((p) => [p.id, p.nome]));
        setMotoristas(
          topMotoristaIds.map((id) => ({
            id,
            nome: nomeMap.get(id) || `Motorista #${id}`,
            osFinalizadas: motoristaCounts[id].osFinalizadas,
            repasseTotal: motoristaCounts[id].repasseTotal,
          }))
        );
      } else {
        setMotoristas([]);
      }

      // 7c. Inadimplência: contas vencidas e ainda não pagas
      const hojeISO = now.toISOString().slice(0, 10);
      const { data: contasVencidas, error: contasErr } = await supabase
        .from('conta')
        .select('id, valor, dt_vencimento, pessoa_id')
        .lt('dt_vencimento', hojeISO)
        .is('dt_pagamento', null);

      if (contasErr) {
        console.warn('[Dashboard] erro ao buscar contas vencidas:', contasErr.message);
        setInadimplencia({ total: 0, count: 0, contas: [] });
      } else {
        const lista = contasVencidas || [];
        const totalVencido = lista.reduce((acc, c) => acc + (Number(c.valor) || 0), 0);
        const top5 = [...lista].sort((a, b) => (Number(b.valor) || 0) - (Number(a.valor) || 0)).slice(0, 5);
        const pessoaIds = [...new Set(top5.map((c) => c.pessoa_id).filter(Boolean))];
        let nomesPessoa = new Map<number, string>();
        if (pessoaIds.length > 0) {
          const { data: pessoasData } = await supabase.from('pessoa').select('id, nome').in('id', pessoaIds);
          nomesPessoa = new Map((pessoasData || []).map((p) => [p.id, p.nome]));
        }
        setInadimplencia({
          total: totalVencido,
          count: lista.length,
          contas: top5.map((c) => ({
            id: c.id,
            nome: nomesPessoa.get(c.pessoa_id) || 'Cliente não identificado',
            valor: Number(c.valor) || 0,
            dt_vencimento: c.dt_vencimento,
          })),
        });
      }

      setStats({
        pessoas: countPessoas || 0,
        osAbertas,
        osAndamento,
        osFinalizadas,
        faturamentoTotal,
        totalVeiculos: countVeiculos || 0,
        veiculosEmRota,
      });

      // 8. Alertas reais (tabela notificacoes), mais recentes primeiro
      const { data: notifs, error: notifErr } = await supabase
        .from('notificacoes')
        .select('id, titulo, mensagem, tipo, created_at')
        .order('created_at', { ascending: false })
        .limit(5);
      if (notifErr) {
        console.warn('[Dashboard] erro ao buscar notificacoes:', notifErr.message);
        setAlertas([]);
      } else {
        setAlertas(notifs || []);
      }

      setLastSync(new Date());
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const disponibilidadePct = stats.totalVeiculos > 0
    ? Math.round((stats.veiculosEmRota / stats.totalVeiculos) * 100)
    : 0;

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-slate-200 pb-5">
        <div>
          <p className="text-[11px] font-mono font-bold tracking-[0.2em] text-red-600 uppercase mb-1">
            Torre de Controle
          </p>
          <h1 className="text-2xl font-black text-slate-900 uppercase tracking-tight">Painel de Operações</h1>
          <p className="mt-1 text-sm text-slate-500">
            Voope TWO Transportes ·{' '}
            {lastSync
              ? `sincronizado às ${lastSync.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
              : 'sincronizando...'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {/* Seletor de Abas do Dashboard Administrativo */}
          <div className="flex items-center bg-slate-200/80 p-1 rounded-xl border border-slate-300 w-full sm:w-auto">
            <button
              onClick={() => setActiveTab('agenda')}
              className={`flex-1 sm:flex-initial px-3.5 py-1.5 rounded-lg text-xs font-bold transition flex items-center justify-center gap-1.5 cursor-pointer ${
                activeTab === 'agenda'
                  ? 'bg-red-600 text-white shadow-md'
                  : 'text-slate-700 hover:text-slate-900'
              }`}
            >
              <CalendarDays className="w-4 h-4 shrink-0" />
              <span>Agenda & Planner</span>
            </button>
            <button
              onClick={() => setActiveTab('metricas')}
              className={`flex-1 sm:flex-initial px-3.5 py-1.5 rounded-lg text-xs font-bold transition flex items-center justify-center gap-1.5 cursor-pointer ${
                activeTab === 'metricas'
                  ? 'bg-slate-900 text-white shadow-md'
                  : 'text-slate-700 hover:text-slate-900'
              }`}
            >
              <LayoutDashboard className="w-4 h-4 shrink-0" />
              <span>Métricas & Indicadores</span>
            </button>
          </div>

          <div className="flex items-center gap-2 ml-auto sm:ml-0">
            <span className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-full text-xs font-bold border border-emerald-100">
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
              Sistema Online
            </span>
            <button
              onClick={fetchStats}
              className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all cursor-pointer"
              aria-label="Atualizar dados"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Exibição da Agenda / Planner */}
      {activeTab === 'agenda' && (
        <AgendaFretesPanel />
      )}

      {/* Exibição das Métricas & KPIs */}
      {activeTab === 'metricas' && (
        <>
          {/* KPI Cards */}
          <div className={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${canViewFinancial ? 'lg:grid-cols-3 2xl:grid-cols-6' : 'lg:grid-cols-3'}`}>
        {canViewFinancial && (
          <KpiCard
            title="Faturamento (histórico)"
            value={formatBRL(stats.faturamentoTotal)}
            icon={DollarSign}
            footnote={`${totalOS.toLocaleString('pt-BR')} OS no total`}
            color="text-emerald-600"
            bg="bg-emerald-50"
          />
        )}
        {canViewFinancial && (
          <KpiCard
            title="Margem Líquida"
            value={margemPct !== null ? `${margemPct.toFixed(1)}%` : '—'}
            icon={Percent}
            footnote="faturamento - repasse, histórico"
            color={margemPct !== null && margemPct < 15 ? 'text-red-600' : 'text-emerald-600'}
            bg={margemPct !== null && margemPct < 15 ? 'bg-red-50' : 'bg-emerald-50'}
          />
        )}
        {canViewFinancial && (
          <KpiCard
            title="Inadimplência"
            value={formatBRL(inadimplencia.total)}
            icon={AlertTriangle}
            footnote={`${inadimplencia.count} conta(s) vencida(s)`}
            color={inadimplencia.count > 0 ? 'text-red-600' : 'text-emerald-600'}
            bg={inadimplencia.count > 0 ? 'bg-red-50' : 'bg-emerald-50'}
          />
        )}
        <KpiCard
          title="OS em Aberto"
          value={stats.osAbertas}
          icon={Clock}
          footnote="Aguardando início"
          color="text-amber-600"
          bg="bg-amber-50"
        />
        <KpiCard
          title="Em Rota Agora"
          value={stats.veiculosEmRota}
          icon={Radio}
          footnote={
            stats.totalVeiculos > 0
              ? `${disponibilidadePct}% da frota · ping últimos 15min`
              : 'sem veículos cadastrados'
          }
          color="text-blue-600"
          bg="bg-blue-50"
        />
        <KpiCard
          title="Base de Clientes"
          value={stats.pessoas}
          icon={Users}
          footnote={`${stats.totalVeiculos.toLocaleString('pt-BR')} veículos cadastrados`}
          color="text-slate-600"
          bg="bg-slate-100"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Faturamento real por mês */}
        {canViewFinancial && (
          <div className="lg:col-span-2 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-bold text-slate-800 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-red-600" />
                Faturamento x Repasse (6 meses)
              </h3>
              <div className="flex items-center gap-4 text-xs font-medium text-slate-500">
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-red-500 rounded-sm" /> Faturamento</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-slate-300 rounded-sm" /> Repasse</span>
              </div>
            </div>
            {monthly.some((m) => m.faturamento > 0) ? (
              <div className="h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthly} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12 }} dy={10} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12 }} />
                    <Tooltip
                      cursor={{ fill: '#f8fafc' }}
                      formatter={(v: number) => formatBRL(v)}
                      contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                    />
                    <Bar dataKey="faturamento" fill="#dc2626" radius={[4, 4, 0, 0]} barSize={24} />
                    <Bar dataKey="repasse" fill="#cbd5e1" radius={[4, 4, 0, 0]} barSize={24} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <EmptyState texto="Sem OS com valores lançados nos últimos 6 meses." />
            )}
          </div>
        )}

        {/* Distribuição de OS — manifesto de status, não donut genérico */}
        <div className={`${canViewFinancial ? 'lg:col-span-1' : 'lg:col-span-3'} bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col`}>
          <div className="flex items-baseline justify-between mb-5">
            <h3 className="font-bold text-slate-800 flex items-center gap-2">
              <FileText className="w-5 h-5 text-red-600" />
              Distribuição de OS
            </h3>
            <span className="font-mono text-xs text-slate-400">{totalOS.toLocaleString('pt-BR')} total</span>
          </div>

          {statusSlices.length === 0 ? (
            <EmptyState texto="Nenhuma OS cadastrada ainda." />
          ) : (
            <ul className="space-y-3 flex-1">
              {statusSlices.map((s) => {
                const pct = totalOS > 0 ? (s.value / totalOS) * 100 : 0;
                return (
                  <li key={s.id}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-slate-600 truncate pr-2">{s.name}</span>
                      <span className="font-mono text-sm font-bold text-slate-900 tabular-nums">
                        {s.value.toLocaleString('pt-BR')}
                      </span>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${Math.max(pct, 2)}%`, backgroundColor: s.cor }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Entregas finalizadas por semana — dado real */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
          <h3 className="font-bold text-slate-800 mb-6 flex items-center gap-2">
            <Package className="w-5 h-5 text-red-600" />
            OS Finalizadas (últimas 4 semanas)
          </h3>
          {weekly.some((w) => w.entregas > 0) ? (
            <div className="h-[250px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={weekly} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorEntregas" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#dc2626" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#dc2626" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12 }} dy={10} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12 }} allowDecimals={false} />
                  <Tooltip />
                  <Area type="monotone" dataKey="entregas" stroke="#dc2626" strokeWidth={3} fillOpacity={1} fill="url(#colorEntregas)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <EmptyState texto="Nenhuma OS finalizada nas últimas 4 semanas." />
          )}
        </div>

        {/* Alertas reais (tabela notificacoes) */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-bold text-slate-800 flex items-center gap-2">
              <Inbox className="w-5 h-5 text-red-600" />
              Alertas e Notificações
            </h3>
            <button className="text-xs font-bold text-red-600 hover:underline">Ver tudo</button>
          </div>
          {alertas.length === 0 ? (
            <EmptyState texto="Nenhuma notificação recente." />
          ) : (
            <div className="space-y-4">
              {alertas.map((a) => {
                const estilo = alertaEstilo(a.tipo);
                return (
                  <div
                    key={a.id}
                    className="flex gap-4 p-3 rounded-xl hover:bg-slate-50 transition-colors border border-transparent hover:border-slate-100 cursor-pointer"
                  >
                    <div className={`flex-shrink-0 w-10 h-10 rounded-lg ${estilo.bg} ${estilo.color} flex items-center justify-center`}>
                      <span className="w-2 h-2 rounded-full bg-current" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <h5 className="text-sm font-bold text-slate-800 truncate">{a.titulo}</h5>
                        <span className="text-[10px] font-medium text-slate-400 whitespace-nowrap">
                          {tempoRelativo(a.created_at)}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 leading-relaxed line-clamp-1">{a.mensagem}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300 self-center" />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Ranking de motoristas por OS finalizada */}
        <div className={`bg-white p-6 rounded-2xl border border-slate-200 shadow-sm ${!canViewFinancial ? 'lg:col-span-2' : ''}`}>
          <h3 className="font-bold text-slate-800 mb-6 flex items-center gap-2">
            <Award className="w-5 h-5 text-red-600" />
            Top Motoristas (OS finalizadas)
          </h3>
          {motoristas.length === 0 ? (
            <EmptyState texto="Nenhuma OS finalizada com motorista vinculado ainda." />
          ) : (
            <ul className="space-y-4">
              {motoristas.map((m, i) => {
                const max = motoristas[0].osFinalizadas || 1;
                const pct = (m.osFinalizadas / max) * 100;
                return (
                  <li key={m.id}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-slate-700 flex items-center gap-2 truncate pr-2">
                        <span className="font-mono text-[10px] text-slate-400 w-4">{`#${i + 1}`}</span>
                        {m.nome}
                      </span>
                      <span className="font-mono text-sm font-bold text-slate-900 tabular-nums">
                        {m.osFinalizadas.toLocaleString('pt-BR')}
                      </span>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full bg-red-500 transition-all" style={{ width: `${Math.max(pct, 4)}%` }} />
                    </div>
                    {canViewFinancial && (
                      <p className="text-[11px] text-slate-400 mt-1">Repasse total: {formatBRL(m.repasseTotal)}</p>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Contas vencidas — detalhe acionável da inadimplência */}
        {canViewFinancial && (
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-bold text-slate-800 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                Contas Vencidas
              </h3>
              {inadimplencia.count > 5 && (
                <span className="text-xs font-mono text-slate-400">+{inadimplencia.count - 5} outras</span>
              )}
            </div>
            {inadimplencia.contas.length === 0 ? (
              <EmptyState texto="Nenhuma conta vencida em aberto. Tudo em dia." />
            ) : (
              <ul className="space-y-3">
                {inadimplencia.contas.map((c) => (
                  <li key={c.id} className="flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 border border-transparent hover:border-slate-100">
                    <div className="min-w-0 pr-2">
                      <p className="text-sm font-bold text-slate-800 truncate">{c.nome}</p>
                      <p className="text-[11px] text-slate-400">
                        venceu em {new Date(c.dt_vencimento).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                    <span className="font-mono text-sm font-bold text-red-600 whitespace-nowrap">
                      {formatBRL(c.valor)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
        </>
      )}
    </div>
  );
}

function KpiCard({ title, value, icon: Icon, footnote, color, bg }: any) {
  return (
    <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all group min-w-0 flex flex-col justify-between">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-xl ${bg} ${color} group-hover:scale-110 transition-transform flex-shrink-0`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
      <div className="min-w-0 flex flex-col">
        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider truncate" title={title}>{title}</h4>
        
        {/* Tooltip Customizado Instantâneo */}
        <div className="relative group/tooltip mt-1">
          <div className="text-xl 2xl:text-2xl font-black text-slate-900 font-mono tabular-nums truncate cursor-default">
            {value}
          </div>
          <div className="pointer-events-none absolute bottom-full left-0 mb-2 z-50 opacity-0 group-hover/tooltip:opacity-100 transition-opacity bg-slate-800 text-white text-xs font-medium px-2.5 py-1 rounded shadow-xl whitespace-nowrap">
            {value}
            {/* Setinha apontando pra baixo */}
            <div className="absolute top-full left-4 -mt-[1px] border-4 border-transparent border-t-slate-800"></div>
          </div>
        </div>

        <p className="text-[11px] text-slate-400 mt-1 truncate" title={footnote}>{footnote}</p>
      </div>
    </div>
  );
}

function EmptyState({ texto }: { texto: string }) {
  return (
    <div className="h-[180px] flex flex-col items-center justify-center text-center gap-2 border border-dashed border-slate-200 rounded-xl">
      <Truck className="w-6 h-6 text-slate-300" />
      <p className="text-xs text-slate-400 max-w-[220px]">{texto}</p>
    </div>
  );
}